import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Outlet } from '@tanstack/react-router';
import {
  SplashScreen,
  View,
  Root,
  AppMenu,
  DataRetentionBanner,
  withTranslation,
  SessionTimeoutModal,
  PaymentFailedBanner,
  TFunction,
  JWTProvider,
  DomainsCertificateExpirationModal,
  Preloader,
  LicenseExpirationGuard,
  CapGuardBanner,
} from '@piwikpro/ui-components';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import {
  withAccess,
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { Session } from '@piwikpro/auth-crate/Session';
import { Role } from '@piwikpro/users-crate';
import { BillingService } from '@piwikpro/marketing-crate/BillingService';
import { Notifications } from '@piwikpro/notification-crate';
import { ReactRouterLink } from '@piwikpro/router-crate/components';
import { AdministrationState } from './redux';

export interface AdministrationProps {
  featureFlags: any
  isAdministrationAvailable: boolean
  t: TFunction
  Title: typeof React.Component
  ProductBar: typeof React.Component
  jwt: SessionState['jwt']
  isLoading: SessionState['isLoading']
  sessionService: Session
  sessionExpiration: SessionState['sessionExpiration']
  accessManager: any
  billingService: BillingService
  userRole: SessionState['userRole']
  hasPaddleSubscription: boolean
  notifications: Notifications
}

interface ITopBarNavItem {
  id: number
  label: string
  value: string
  userRole: Role
}

export const Administration: React.FunctionComponent<AdministrationProps> = ({
  featureFlags,
  isAdministrationAvailable,
  t,
  Title,
  ProductBar,
  jwt,
  isLoading,
  sessionService,
  sessionExpiration,
  accessManager,
  billingService,
  notifications,
  hasPaddleSubscription,
  userRole,
}) => {
  useEffect(() => {
    const fetchData = async () => {
      try {
        await featureFlags.fetchFeatureFlags();
        await billingService.getCurrentSubscriptionTier();
        // eslint-disable-next-line no-empty
      } catch { }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return <Preloader />;
  }

  const isUserRoleUser = userRole === 'USER';
  const isOwnerRole = userRole === 'OWNER';
  // user role should be able to preview billing view for Paddle subscription
  const showAccountTabForUser = isOwnerRole || (isUserRoleUser && hasPaddleSubscription);

  const topBarNavItems = [
    {
      id: 1,
      label: t('navigation.sections.apps'),
      value: '/administration/apps',
      userRole: 'USER',
    },
    {
      id: 2,
      label: t('navigation.sections.meta-sites'),
      value: '/administration/meta-sites',
      userRole: 'USER',
    },
    {
      id: 3,
      label: t('navigation.sections.users'),
      value: '/administration/users',
      userRole: 'OWNER',
    },
    {
      id: 4,
      label: t('navigation.sections.user-groups'),
      value: '/administration/user-groups',
      userRole: 'OWNER',
    },
    showAccountTabForUser && {
      id: 5,
      label: t('navigation.sections.account'),
      value: '/administration/account',
      userRole: 'USER',
    },
  ].filter(item => item && accessManager.isGranted({ role: item.userRole })) as ITopBarNavItem[];

  return (
    <JWTProvider jwt={jwt!} refreshJWT={sessionService.refreshJWT}>
      <Root title={null} withThemes>
        <Root.TopSection>
          <ProductBar>{t('moduleName')}</ProductBar>
          <DataRetentionBanner
            onContactFormSubmitSuccess={({ message }) => {
              notifications.push({
                text: message,
                type: 'success',
                autoCloseSeconds: 5,
              });
            }}
            onContactFormSubmitFail={({ message }) => {
              notifications.push({
                text: message,
                type: 'error',
                autoCloseSeconds: 5,
              });
            }}
          />
          <SessionTimeoutModal
            sessionExpiration={sessionExpiration}
            onLogout={() => sessionService.logout()}
            onUpdateToken={() => sessionService.getSession().catch(() => {
              sessionService.logout();
            })}
            updateTokenPending={isLoading}
          />
          <DomainsCertificateExpirationModal />
          <LicenseExpirationGuard onLogout={() => sessionService.logout()} />
          <PaymentFailedBanner />
          <CapGuardBanner />
          {isAdministrationAvailable && (
            <div id="top-section-nav">
              <AppMenu
                routerMode="v7"
                tabs={topBarNavItems}
                RouterLink={ReactRouterLink}
              />
            </div>
          )}
        </Root.TopSection>
        <Root.Content>
          <Title part={t('moduleName')}>
            {isAdministrationAvailable ? (
              <Outlet />
            ) : (
              <View fullHeight>
                <SplashScreen type="noPrivilegesModule" />
              </View>
            )}
          </Title>
        </Root.Content>
      </Root>
    </JWTProvider>
  );
};

const mapStateToProps = (state: AdministrationState) => ({
  isAdministrationAvailable: !!state.session.modules?.find(
    element => element.id === 'administration',
  ),
  jwt: state.session.jwt,
  isLoading: state.session.isLoading || !Array.isArray(state.session.modules),
  sessionExpiration: state.session.sessionExpiration,
  hasPaddleSubscription: Boolean(state.payments.subscription.details),
  userRole: state.session.userRole,
});

export default withInject({
  ProductBar: 'AuthCrate.components.ProductBar',
  Title: 'TitleCrate.components.Title',
  featureFlags: 'FeatureFlagsCrate.featureFlags',
  sessionService: 'AuthCrate.session',
  billingService: 'MarketingCrate.billingService',
  notifications: 'NotificationCrate.notifications',
})(
  connect(mapStateToProps, null)(withAccess(withTranslation('administration')(
    errorBoundary(Administration),
  ))),
);
